import React, { FC, ForwardRefRenderFunction, PropsWithChildren } from 'react';
import { Box, Divider, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useMediaQuery, useTheme } from '@mui/material';
import Product from '../../../../../@types/Product';
import Button from '../../../../common/Button';
import { useFormContext, useWatch } from 'react-hook-form';
import useCartModelDisplay from '../../../../../hooks/useCartModelDisplay';
import assets from '../../../../../assets';
import NumberInput from '../../../../common/Form/FormFields/NumberInput/NumberInput';
import useCurrencyConverter from '../../../../../hooks/useCurrencyConverter';
import useAuth from '../../../../../hooks/useAuth';

interface CartProductSummaryProps {
  retailConfigurator: boolean,
  fromInventory: boolean,
  editingCart: boolean,
  hideAllPrices: boolean
}

const CartProductSummary: ForwardRefRenderFunction<HTMLDivElement, CartProductSummaryProps> = (
  { retailConfigurator, fromInventory, editingCart, hideAllPrices },
  ref
) => {
  const { convertCurrency } = useCurrencyConverter();
  const {
    chassisType,
    bodyColor,
    liftKit,
    wheel,
    frontSeat
  } = useCartModelDisplay();
  const { getValues } = useFormContext();
  const { user } = useAuth();
  //TEMP remove hasLicenses to prevent LSV orders Per Erica by setting false
  const hasLicenses = false; //user?.companyAttributes?.licenses !== undefined && user?.companyAttributes?.licenses?.length > 0;
  const batteryOption: Product | undefined = getValues('chassisBatteryConfig');
  const tireOption: Product | undefined = getValues('tire');
  const lugNutOption: Product | undefined = getValues('lugNut');
  const includedAccessories = useWatch({ name: 'includedAccessories' });
  const optionalAccessories = useWatch({ name: 'optionalAccessories' });
  const cartPrice = useWatch({ name: 'cartPrice' });
  const total = useWatch({ name: 'total' });
  const lsvKitOption: Product | undefined = getValues('lsvKit');

  return (
    <div ref={ref}>
      <Stack direction="column" spacing={2} sx={{}}>
        {batteryOption &&
          <CategorySection title="Power">
            <CategoryTableProducts
              retailConfigurator={retailConfigurator}
              category="Battery"
              products={batteryOption ? [batteryOption] : []}
              hideAllPrices={hideAllPrices} />
          </CategorySection>
        }
        {(bodyColor || liftKit || wheel || tireOption) &&
          <CategorySection title="Exterior">
            {bodyColor &&
              <CategoryTableProducts
                retailConfigurator={retailConfigurator}
                category="Body Color"
                products={bodyColor ? [bodyColor] : []}
                hideAllPrices={hideAllPrices} />
            }
            {liftKit &&
              <CategoryTableProducts
                retailConfigurator={retailConfigurator}
                category="Lift Kit"
                products={liftKit ? [liftKit] : []}
                hideAllPrices={hideAllPrices} />
            }
            {wheel &&
              <CategoryTableProducts
                retailConfigurator={retailConfigurator}
                category="Wheels"
                products={wheel && lugNutOption ? [wheel, lugNutOption] : []}
                hideAllPrices={hideAllPrices} />
            }
            {tireOption &&
              <CategoryTableProducts
                retailConfigurator={retailConfigurator}
                category="Tires"
                products={tireOption ? [tireOption] : []}
                hideAllPrices={hideAllPrices} />
            }
          </CategorySection>
        }
        {frontSeat &&
          <CategorySection title="Interior">
            <CategoryTableProducts
              retailConfigurator={retailConfigurator}
              category="Seats"
              products={frontSeat ? [frontSeat] : []}
              hideAllPrices={hideAllPrices} />
          </CategorySection>
        }
        {((optionalAccessories?.length > 0) || (includedAccessories?.length > 0)) &&
          <CategorySection title="Accessories">
            <CategoryTableProducts
              retailConfigurator={retailConfigurator}
              category="Included" products={includedAccessories}
              displayPrice={false}
              hideAllPrices={hideAllPrices} />
            {(optionalAccessories?.length > 0) &&
              <CategoryTableProducts
                retailConfigurator={retailConfigurator}
                category="Optional"
                products={optionalAccessories}
                hideAllPrices={hideAllPrices} />
            }
          </CategorySection>
        }
        {(!retailConfigurator && hasLicenses && lsvKitOption) &&
          <CategorySection title="LSV">
            <CategoryTableProducts
              retailConfigurator={retailConfigurator}
              category="LSV Kit" products={lsvKitOption ? [lsvKitOption] : []}
              hideAllPrices={hideAllPrices} />
          </CategorySection>
        }
        {!retailConfigurator &&
          <Stack alignItems="flex-end">
            <NumberInput
              name="quantity"
              label="# Carts"
              disabled={fromInventory}
              sx={(theme) => ({
                [theme.breakpoints.down('tablet')]: {
                  '& Button': {
                    width: '50px'
                  }
                }
              })} />
          </Stack>
        }
        {!hideAllPrices &&
          <Stack
            direction="column"
            spacing={2}
            sx={{
              background: "#ECECEC",
              padding: "2%",
            }}>
            <Stack direction="column" spacing={2}>
              <Typography
                variant="primaryFont"
                fontWeight="bold">
                Pricing Summary
              </Typography>
              <Divider flexItem />
              <>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="primaryFont">
                    {retailConfigurator
                      ? `Base MSRP (US Pricing)`
                      : `Base Cart Price`
                    }
                  </Typography>
                  <Typography variant="primaryFont">
                    {retailConfigurator
                      ? convertCurrency(chassisType?.retailPrice ?? 0)
                      : convertCurrency(chassisType?.price ?? 0)}*
                  </Typography>
                </Stack>
                {!retailConfigurator &&
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="primaryFont" fontWeight="bold">
                      Total Cart Price
                    </Typography>
                    <Typography variant="primaryFont" fontWeight="bold">
                      {convertCurrency(cartPrice ?? 0)}*
                    </Typography>
                  </Stack>
                }
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="primaryFont" fontWeight="bold">
                    {retailConfigurator
                      ? `Estimated MSRP (as shown)`
                      : `Total`
                    }
                  </Typography>
                  <Typography variant="primaryFont" fontWeight="bold">
                    {convertCurrency(total ?? 0)}*
                  </Typography>
                </Stack>
                <Typography variant="primaryFont" fontStyle="italic" fontSize={12}>
                  {retailConfigurator
                    ? `* Price does not include taxes, dealer preps, delivery, or other fees.`
                    : `* Expected pricing`
                  }
                </Typography>
                {(process.env.REACT_APP_DEFAULT_COUNTRY === 'US' || process.env.REACT_APP_DEFAULT_COUNTRY === 'CA') &&
                  <Stack>
                    <Button type="submit">
                      {retailConfigurator
                        ? `Save My Build`
                        : editingCart
                          ? 'Update order'
                          : `Add to order`}
                    </Button>
                  </Stack>
                }
              </>
            </Stack>
          </Stack>
        }
      </Stack>
    </div>
  );
};

interface CategorySectionProps {
  title: string
}

const CategorySection: FC<PropsWithChildren<CategorySectionProps>> = ({ children, title }) => {
  return (
    <Stack direction="column" spacing={6} justifyContent="center" alignItems="center">
      <Typography
        fontSize={16}
        letterSpacing="4px"
        variant="primaryFont"
        textTransform="uppercase">
        {title}
      </Typography>
      {children}
    </Stack>
  );
}

interface CategoryTableProductsProps {
  category: string;
  displayPrice?: boolean;
  products: Product[]
  retailConfigurator: boolean;
  hideAllPrices: boolean;
}

const CategoryTableProducts: FC<CategoryTableProductsProps> = ({ category, products, displayPrice = true, retailConfigurator, hideAllPrices }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('mobile'));
  const { convertCurrency } = useCurrencyConverter();
  const tableHeaders: string[] = hideAllPrices ? ['Category', 'Option', ''] : ['Category', 'Option', 'Price'];
  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            {tableHeaders.map((header, index) => (
              <TableCell
                key={index}
                sx={{
                  padding: '0px 16px 0px 16px',
                  borderLeft: 'none',
                  borderRight: 'none',
                  borderTop: 'none',
                  ...((index === 0)) && {
                    width: 90
                  },
                  ...((index === tableHeaders.length - 1)) && {
                    textAlign: 'right',
                    paddingRight: '24px',
                  }
                }}>
                {header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {products?.map((product, index) => (
            <TableRow key={index} sx={{
              '& .MuiTableCell-root': {
                padding: '8px 16px 16px 16px',
                border: '1px solid transparent'
              },
              '& .MuiTableCell-root:not(:first-of-type)': {
                borderBottom: '1px solid rgb(224, 224, 224)',
                ...((index === products.length - 1)) && {
                  border: '1px solid transparent'
                }
              }
            }}>
              <TableCell sx={{ verticalAlign: 'top' }}>
                <Typography
                  variant="secondaryFont"
                  fontWeight="bold">
                  {index === 0 ? `${category}` : ''}
                </Typography>
              </TableCell>
              <TableCell>
                <Stack
                  direction="row"
                  spacing={1}
                  useFlexGap
                  flexWrap="wrap">
                  {!isMobile &&
                    (product.img
                      ? (
                        <img draggable="false"
                          src={product.img}
                          alt={`${product.name}`}
                          width={`${75}px`}
                          height={`${75}px`} />
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: `${75}px`,
                            minHeight: `${75}px`,
                            backgroundColor: '#D9D9D9'
                          }}>
                          <img src={assets.images.missingImage} alt='missing' />
                        </Box>
                      )
                    )
                  }
                  <Typography fontSize={14}>{product.name}</Typography>
                </Stack>
              </TableCell>
              {!hideAllPrices &&
                <TableCell
                  sx={{
                    verticalAlign: 'top',
                    textAlign: 'right',
                    padding: '8px 24px 16px 16px',
                  }}>
                  {displayPrice &&
                    <Typography fontSize={14}>
                      {retailConfigurator &&
                        (product.retailPrice === 0
                          ? ('~')
                          : (convertCurrency(product.retailPrice ?? 0)))
                      }
                      {!retailConfigurator &&
                        (product.price === 0
                          ? ('~')
                          : (convertCurrency(product.price)))
                      }
                    </Typography>
                  }
                  {!displayPrice &&
                    <Typography fontSize={14}>Included</Typography>
                  }
                </TableCell>
              }
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}


const ForwardedCartProductSummary = React.forwardRef(CartProductSummary);

export default ForwardedCartProductSummary;
